import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Chip, Divider } from '@mui/material';
import { Close, Psychology, Message, Sms, Description, Person, Business, Send, ThumbUp } from '@mui/icons-material';
import { enhancedApi as adminApi } from '@store/generated/admin';
import { formatDateTime } from '../utils/formatting';
import { JotCoachFeedbackItem, type FeedbackItem } from './JotCoachFeedbackItem';
import type { JotCoachDocumentationAnalysis } from '@features/jotcoach';

const { useGetV2AdminJotcoachSessionsBySessionIdQuery } = adminApi;

interface JotCoachSessionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
}

export function JotCoachSessionDetailDialog({ open, onClose, sessionId }: JotCoachSessionDetailDialogProps) {
  const {
    data: session,
    isLoading,
    error,
  } = useGetV2AdminJotcoachSessionsBySessionIdQuery(
    { sessionId: sessionId || '' },
    { skip: !sessionId || !open }
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Psychology color="primary" />
            <Typography variant="h6">JotCoach Session Details</Typography>
          </Box>
          <Button onClick={onClose} size="small" startIcon={<Close />}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box py={4}>
            <Typography color="error" align="center">
              Failed to load session details
            </Typography>
          </Box>
        )}

        {session && (
          <Box>
            {/* Session Metadata */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Session Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 150 }}>
                    Session ID:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                    {session.jotcoach_session_id}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 135 }}>
                    User:
                  </Typography>
                  <Box>
                    {session.user_name && (
                      <Typography variant="body2">
                        {session.user_name}
                      </Typography>
                    )}
                    {session.user_email && (
                      <Typography variant="caption" color="text.secondary">
                        {session.user_email}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block">
                      ID: {session.user_id}
                    </Typography>
                  </Box>
                </Box>
                {session.organization_id && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Business fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 135 }}>
                      Organization:
                    </Typography>
                    <Box>
                      {session.organization_name && (
                        <Typography variant="body2">
                          {session.organization_name}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        ID: {session.organization_id}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {session.note_id && (
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 150 }}>
                      Note ID:
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                      {session.note_id}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 150 }}>
                    Created:
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(session.created_at)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 150 }}>
                    Last Updated:
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(session.updated_at)}
                  </Typography>
                </Box>
                {session.sent_at && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Send fontSize="small" color="success" />
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 135 }}>
                      SMS Sent:
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      {formatDateTime(session.sent_at)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Gap Analysis */}
            {session.session_data.gap_analysis && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Description fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Billing Gap Analysis
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {session.session_data.gap_analysis}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* SMS Nudge */}
            {session.session_data.sms_nudge && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Sms fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    SMS Nudge
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'primary.50',
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'primary.200',
                  }}
                >
                  <Typography variant="body2">
                    {session.session_data.sms_nudge}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Message History */}
            {session.session_data.message_history && session.session_data.message_history.length > 0 && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Message fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Message History ({session.session_data.message_history.length})
                  </Typography>
                </Box>
                <Box
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {session.session_data.message_history.map((message, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        borderBottom: index < session.session_data.message_history!.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        bgcolor: index % 2 === 0 ? 'grey.50' : 'white',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={message.role || 'unknown'}
                          size="small"
                          color={message.role === 'assistant' ? 'primary' : 'default'}
                        />
                        {message.timestamp && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(message.timestamp)}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content || JSON.stringify(message)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Session Analysis */}
            {session.session_data.session_analysis && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Session Analysis
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(session.session_data.session_analysis, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}

            {/* Documentation Analysis */}
            {session.session_data.documentation_analysis && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Documentation Analysis
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(session.session_data.documentation_analysis, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}

            {/* Synthesized Feedback */}
            {session.session_data.synthesized_feedback && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Synthesized Feedback
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(session.session_data.synthesized_feedback, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}

            {/* User Feedback */}
            {session.session_data.user_feedback && session.session_data.user_feedback.length > 0 ? (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ThumbUp fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    User Feedback ({session.session_data.user_feedback.length} items)
                  </Typography>
                </Box>
                <Box>
                  {session.session_data.user_feedback.map((feedbackItem, index) => (
                    <JotCoachFeedbackItem
                      key={index}
                      feedbackItem={feedbackItem as FeedbackItem}
                      documentationAnalysis={session.session_data.documentation_analysis as JotCoachDocumentationAnalysis | undefined}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              session.session_data.documentation_analysis && (
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ThumbUp fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      User Feedback
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No feedback submitted yet
                  </Typography>
                </Box>
              )
            )}

            {/* Empty state */}
            {!session.session_data.gap_analysis &&
             !session.session_data.sms_nudge &&
             !session.session_data.session_analysis &&
             !session.session_data.documentation_analysis &&
             !session.session_data.synthesized_feedback &&
             (!session.session_data.user_feedback || session.session_data.user_feedback.length === 0) &&
             (!session.session_data.message_history || session.session_data.message_history.length === 0) && (
              <Box py={4}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No session data available
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
